import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden lg:flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden lg:flex">
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn("group inline-flex h-9 w-max items-center justify-center px-2 py-1 text-sm font-semibold transition-all", "text-foreground/80 hover:text-foreground", "border-b-2 border-transparent hover:border-primary", "focus:outline-none disabled:pointer-events-none disabled:opacity-50")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted/50 animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-2 p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="block py-3 text-lg font-medium text-foreground hover:text-primary transition-colors border-b border-border/40" onClick={onClose}>
          {item.label}
        </Link>)}
      <div className="mt-6">
        <Button className="w-full bg-primary text-primary-foreground rounded-[4px] font-bold" asChild>
          <Link to="/GetAQuotePage">Free Quote</Link>
        </Button>
      </div>
    </nav>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 ease-in-out border-b", isScrolled ? "bg-background/80 backdrop-blur-xl border-primary/5 shadow-sm py-3" : "bg-background border-transparent py-5")}>
      <div className="container mx-auto px-4 md:px-6 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 z-50 flex items-center">
          <img src="https://mature-leech.10web.cloud/wp-content/uploads/2026/02/ANV-Painting-Logo-Large.jpg" alt="ANV Painting" className="object-contain !w-[500px] !h-[100px]" />
          
        </Link>

        {}
        <div className="hidden lg:block">
          <MenuProvider menu_id="17">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:block">
          <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-[4px] font-bold shadow-sm px-6">
            <Link to="/GetAQuotePage">Free Quote</Link>
          </Button>
        </div>

        {}
        <Button variant="ghost" size="icon" className="lg:hidden text-foreground hover:text-primary hover:bg-transparent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </Button>
      </div>

      {}
      <div className={cn("absolute inset-x-0 top-[100%] h-[calc(100vh-100%)] bg-background/95 backdrop-blur-xl border-t border-border/10 transition-all duration-300 ease-in-out lg:hidden overflow-y-auto", isMobileMenuOpen ? "opacity-100 translate-y-0 visible" : "opacity-0 -translate-y-4 invisible pointer-events-none")} style={{
      maxHeight: "calc(100vh - 80px)"
    }}>
        <MenuProvider menu_id="17">
          <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </header>;
}